import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'

export interface DocSearchResult {
  page: PageDocument
  /** Ancestor titles, root first — so a match five levels deep is still placeable. */
  path: Array<string>
  titleMatch: boolean
  /** Body excerpt around the first match, when the title didn't match. */
  snippet: { before: string; match: string; after: string } | null
}

const SNIPPET_CONTEXT = 28
const MAX_RESULTS = 20

function ancestorTitles(page: PageDocument, byId: Map<string, PageDocument>, untitled: string): Array<string> {
  const path: Array<string> = []
  const seen = new Set<string>([page.id])
  let parent = page.parentPageId ? byId.get(page.parentPageId) : undefined
  while (parent && !seen.has(parent.id)) {
    seen.add(parent.id)
    path.unshift(parent.title || untitled)
    parent = parent.parentPageId ? byId.get(parent.parentPageId) : undefined
  }
  return path
}

function excerpt(flat: string, at: number, length: number): DocSearchResult['snippet'] {
  const start = Math.max(0, at - SNIPPET_CONTEXT)
  const end = Math.min(flat.length, at + length + SNIPPET_CONTEXT)
  return {
    before: (start > 0 ? '…' : '') + flat.slice(start, at).trimStart(),
    match: flat.slice(at, at + length),
    after: flat.slice(at + length, end).trimEnd() + (end < flat.length ? '…' : ''),
  }
}

/**
 * Title and body search over a space's docs (design 4c). Title matches rank
 * first; within each group the tree order is kept.
 */
export function searchDocs(
  pages: Array<PageDocument>,
  bodyByPage: Map<string, string>,
  query: string,
  untitled: string,
): Array<DocSearchResult> {
  const needle = query.trim().toLowerCase()
  if (!needle) return []

  const byId = new Map(pages.map((p) => [p.id, p]))
  const titleHits: Array<DocSearchResult> = []
  const bodyHits: Array<DocSearchResult> = []

  for (const page of pages) {
    const title = page.title || untitled
    if (title.toLowerCase().includes(needle)) {
      titleHits.push({ page, path: ancestorTitles(page, byId, untitled), titleMatch: true, snippet: null })
      continue
    }
    const body = (bodyByPage.get(page.id) ?? '').replace(/\s+/g, ' ')
    const index = body.toLowerCase().indexOf(needle)
    if (index >= 0) {
      bodyHits.push({
        page,
        path: ancestorTitles(page, byId, untitled),
        titleMatch: false,
        snippet: excerpt(body, index, needle.length),
      })
    }
  }

  return [...titleHits, ...bodyHits].slice(0, MAX_RESULTS)
}

/** Splits text around case-insensitive occurrences of `query`, for <mark>ing. */
export function splitOnMatch(text: string, query: string): Array<{ text: string; match: boolean }> {
  const needle = query.trim().toLowerCase()
  if (!needle) return [{ text, match: false }]
  const parts: Array<{ text: string; match: boolean }> = []
  const lower = text.toLowerCase()
  let from = 0
  let at = lower.indexOf(needle)
  while (at >= 0) {
    if (at > from) parts.push({ text: text.slice(from, at), match: false })
    parts.push({ text: text.slice(at, at + needle.length), match: true })
    from = at + needle.length
    at = lower.indexOf(needle, from)
  }
  if (from < text.length) parts.push({ text: text.slice(from), match: false })
  return parts
}

/** Number of pages nested (at any depth) under each page. */
export function countDescendants(pages: Array<PageDocument>): Map<string, number> {
  const children = new Map<string, Array<string>>()
  for (const p of pages) {
    if (!p.parentPageId) continue
    const list = children.get(p.parentPageId) ?? []
    list.push(p.id)
    children.set(p.parentPageId, list)
  }
  const memo = new Map<string, number>()
  const count = (id: string, seen: Set<string>): number => {
    const cached = memo.get(id)
    if (cached !== undefined) return cached
    let total = 0
    for (const child of children.get(id) ?? []) {
      if (seen.has(child)) continue
      seen.add(child)
      total += 1 + count(child, seen)
    }
    memo.set(id, total)
    return total
  }
  for (const p of pages) count(p.id, new Set([p.id]))
  return memo
}

/** Ids of every ancestor of `pageId`, nearest first. */
export function ancestorIds(pages: Array<PageDocument>, pageId: string): Array<string> {
  const byId = new Map(pages.map((p) => [p.id, p]))
  const ids: Array<string> = []
  let parentId = byId.get(pageId)?.parentPageId ?? null
  while (parentId && !ids.includes(parentId)) {
    ids.push(parentId)
    parentId = byId.get(parentId)?.parentPageId ?? null
  }
  return ids
}
